import { useCallback, useEffect, useState } from 'react'
import type { ChangeEvent } from 'react'

import { DatePicker } from '../lib/DatePicker'
import { formatUtcAsThai } from '../lib/datetime'
import { canEdit } from '../lib/nav'
import { Pager } from '../lib/Pager'
import { SortBar, SortTh, useSort } from '../lib/Sort'
import { useAuthedSession, useSession } from '../lib/SessionContext'
// ชิ้นส่วนกลางอยู่ในโฟลเดอร์ offsite/ เพราะเป็นที่แรกที่ใช้ — generic ทั้งคู่ ใช้ซ้ำได้
// (ถ้าวันหนึ่งมีหน้าที่สามที่ต้องใช้ ค่อยย้ายขึ้นไป src/lib/ ทีเดียว)
import { SectionHead, ViewerNote } from './offsite/parts'
import { MappingCard } from './walkrun/MappingCard'
import { BASE, errorText, nf } from './walkrun/types'
import type { Paged } from './walkrun/types'

/* ============================================================================
   Walk & Run — อัป CSV ก้าว/ระยะทาง เพื่อเป็นข้อมูลของ dashboard
   ตาราง: backend/sql/mssql/007_walkrun.sql · API: backend/src/routes/adminWalkrun.js

   ⚠️ ไฟล์ไม่มีรหัสพนักงาน มีแต่ชื่อที่ตั้งเองในแอปนับก้าว ระบบจึงยึด "ชื่อ" เป็นตัวตน
      หน้านี้แสดงชื่อตามไฟล์ตรง ๆ ไม่พยายามเดาว่าเป็นพนักงานคนไหน

   ⚠️ ทั้งสองตารางแบ่งหน้าที่ฝั่ง server และรับ total มาด้วย — ตัวเลขสรุปด้านหัวตาราง
      ต้องมาจาก total/totalSteps ของ API ห้ามคำนวณจากแถวที่โหลดมา ไม่งั้นพอมีหน้าที่
      สองตัวเลขจะผิดโดยที่มองไม่ออกจากหน้าจอ
   ============================================================================ */

type Coverage = {
  firstDay: string | null
  lastDay: string | null
  people: number
  dayRows: number
}

type ImportRow = {
  id: number
  fileName: string | null
  rowCount: number
  dayCount: number
  periodFrom: string | null
  periodTo: string | null
  /** UTC — ร่องรอยระบบ (ดูกติกาเวลาใน 007) จึงติดป้ายไว้ตรง ๆ ไม่แปลงเอง */
  importedAtUtc: string | null
  importedBy: string
}

type LeaderRow = {
  nameKey: string
  name: string
  steps: number
  days: number
  firstDay: string
  lastDay: string
}

type ImportResult = {
  importId: number
  people: number
  days: string[]
  /** จำนวนช่อง (คน × วัน) ที่อ่านได้จากไฟล์ */
  dayRows: number
  /** เขียนใหม่ (ยังไม่เคยมีวันนั้นของคนนั้น) */
  inserted: number
  /** ยกค่าขึ้น (ไฟล์นี้มากกว่าที่เก็บไว้) */
  raised: number
  /** ไม่ทับ เพราะค่าในไฟล์ไม่มากกว่าที่เก็บไว้ */
  keptLower: number
  periodFrom: string
  periodTo: string
}

/** ก้าว → กม. คร่าว ๆ สำหรับดูเทียบ (ไฟล์มีคอลัมน์ กม. จริงอยู่แล้วในตาราง walkrun_person) */
const KM_PER_STEP = 0.000731 // จากไฟล์: 20250 ก้าว = 14.81 กม.

export default function Walkrun() {
  const session = useAuthedSession()
  // call() มาจาก useSession() ไม่ใช่จาก session — ตัวนั้นเคลียร์ session ที่เดียวเมื่อเจอ 401
  const { call } = useSession()
  const writable = canEdit(session)

  const [coverage, setCoverage] = useState<Coverage | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [imports, setImports] = useState<Paged<{ imports: ImportRow[] }> | null>(null)
  const [impPage, setImpPage] = useState(1)
  /* สองตารางบนหน้านี้เรียงกันคนละแบบ — state คนละชุด ไม่ใช้ร่วมกัน
     (เรียงประวัติการอัปแล้วอันดับพลิกตามคือเรื่องที่ไม่มีใครคาด) */
  const impSort = useSort([{ key: 'id', dir: 'desc' }], () => setImpPage(1))
  const [impLoading, setImpLoading] = useState(false)

  const [board, setBoard] = useState<Paged<{ leaderboard: LeaderRow[]; totalSteps: number }> | null>(null)
  const [boardPage, setBoardPage] = useState(1)
  const [boardSize, setBoardSize] = useState(10)
  const boardSort = useSort([{ key: 'steps', dir: 'desc' }], () => setBoardPage(1))
  const [boardLoading, setBoardLoading] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const [busy, setBusy] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)

  const loadCoverage = useCallback(async () => {
    try {
      const res = await call<{ coverage: Coverage }>(BASE)
      setCoverage(res.coverage)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    }
  }, [call])

  const loadImports = useCallback(async () => {
    setImpLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(impPage), pageSize: '10' })
      if (impSort.param) qs.set('sort', impSort.param)
      setImports(await call<Paged<{ imports: ImportRow[] }>>(`${BASE}/imports?${qs}`))
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setImpLoading(false)
    }
  }, [call, impPage, impSort.param])

  const loadBoard = useCallback(async () => {
    setBoardLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(boardPage), pageSize: String(boardSize) })
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      if (boardSort.param) qs.set('sort', boardSort.param)
      setBoard(await call<Paged<{ leaderboard: LeaderRow[]; totalSteps: number }>>(`${BASE}/leaderboard?${qs}`))
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setBoardLoading(false)
    }
  }, [call, boardPage, boardSize, from, to, boardSort.param])

  useEffect(() => {
    void loadCoverage()
  }, [loadCoverage])
  useEffect(() => {
    void loadImports()
  }, [loadImports])
  useEffect(() => {
    void loadBoard()
  }, [loadBoard])

  /** เปลี่ยนตัวกรอง/ขนาดหน้า ต้องกลับไปหน้า 1 ไม่งั้นค้างอยู่หน้าที่ไม่มีข้อมูลแล้ว */
  function changeRange(next: { from?: string; to?: string }) {
    if (next.from !== undefined) setFrom(next.from)
    if (next.to !== undefined) setTo(next.to)
    setBoardPage(1)
  }

  async function pickFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // เคลียร์ input ทันทีเพื่อให้เลือกไฟล์ชื่อเดิมซ้ำได้ (ไม่งั้น onChange ไม่ยิงรอบสอง)
    event.target.value = ''
    if (!file) return

    setBusy(true)
    setUploadError(null)
    setResult(null)
    try {
      const csv = await file.text()
      // file.text() decode เป็น UTF-8 เสมอ — ไฟล์ไทยที่ export เป็น TIS-620 จะได้ตัวพัง
      // ดักไว้ตรงนี้ เพราะถ้าปล่อยผ่านจะได้ชื่อคนเป็นขยะแล้วกลายเป็น "คนใหม่" ในฐาน
      if (csv.includes('�')) {
        throw new Error(
          'ไฟล์นี้ไม่ใช่ UTF-8 (ชื่อภาษาไทยจะเพี้ยน) — เปิดด้วย Excel แล้ว Save As ' +
            '“CSV UTF-8 (Comma delimited)” ก่อนอัปใหม่',
        )
      }

      const res = await call<{ result: ImportResult }>(`${BASE}/import`, {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, csv }),
      })
      setResult(res.result)
      setImpPage(1)
      setBoardPage(1)
      await Promise.all([loadCoverage(), loadImports(), loadBoard()])
    } catch (err) {
      setUploadError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const rankOffset = board ? (board.page - 1) * board.pageSize : 0

  return (
    <>
      <h1>Walk &amp; Run</h1>
      <p className="meta">
        Upload CSV ที่ export จากแอปนับก้าว → เก็บเป็นก้าวรายวันของแต่ละคน แล้ว dashboard
        ใน LINE อ่านจากข้อมูลชุดนี้ · อัปไฟล์ที่ครอบวันซ้ำกันได้ ระบบเก็บค่ามากสุดของวันนั้น
      </p>

      {!writable && <ViewerNote />}
      {loadError && <p className="error">โหลดข้อมูลไม่สำเร็จ — {loadError}</p>}

      {/* ── อัปไฟล์ ── */}
      {writable && (
        <section className="card">
          <h2>Upload CSV</h2>
          <label>
            เลือกไฟล์
            <input type="file" accept=".csv,text/csv" onChange={pickFile} disabled={busy} />
            <small className="hint">
              ต้องมีคอลัมน์ <code>Name</code> และคอลัมน์วันที่รูปแบบ <code>YYYY-MM-DD</code>{' '}
              อย่างน้อยหนึ่งคอลัมน์ · บันทึกเป็น <strong>CSV UTF-8</strong> ·
              ไฟล์เดิมที่อัปแล้วจะถูกปฏิเสธ (เทียบจากเนื้อไฟล์ ไม่ใช่ชื่อไฟล์)
            </small>
          </label>

          {busy && <p className="meta">กำลังอ่านไฟล์และบันทึก…</p>}
          {uploadError && <p className="error">{uploadError}</p>}

          {result && (
            <div className="ok">
              <strong>อัปสำเร็จ (ไฟล์ที่ #{result.importId})</strong>
              <dl>
                <dt>คนในไฟล์</dt>
                <dd>{nf.format(result.people)} คน</dd>
                <dt>ช่วงวันที่</dt>
                <dd>
                  {result.periodFrom} – {result.periodTo} ({result.days.length} วัน)
                </dd>
                <dt>ช่องที่อ่านได้จากไฟล์</dt>
                <dd>
                  {nf.format(result.dayRows)} ช่อง (คน × วัน)
                  {result.dayRows < result.people * result.days.length && (
                    <> — น้อยกว่า คน×วัน เพราะช่องที่เป็น <code>N.A</code> ถูกข้าม (ยังไม่มีข้อมูล ไม่ใช่ศูนย์ก้าว)</>
                  )}
                </dd>
                <dt>บันทึกใหม่</dt>
                <dd>{nf.format(result.inserted)} ช่อง</dd>
                <dt>ยกค่าขึ้น</dt>
                <dd>{nf.format(result.raised)} ช่อง (ไฟล์นี้มากกว่าที่เก็บไว้)</dd>
                <dt>ไม่ทับ</dt>
                <dd>{nf.format(result.keptLower)} ช่อง (ค่าในไฟล์ไม่มากกว่าที่เก็บไว้ — เก็บค่าเดิมไว้)</dd>
              </dl>

              {/* เลข "ไม่ทับ" ทั้งหมดคือสัญญาณว่าเลือกไฟล์รอบเก่า ต้องบอกให้เห็น
                  ไม่ใช่ปล่อยให้คนอัปคิดว่าอัปแล้วข้อมูลเข้าเรียบร้อย */}
              {result.keptLower > 0 && result.inserted === 0 && result.raised === 0 && (
                <p className="warn">
                  ไฟล์นี้ไม่ได้เปลี่ยนข้อมูลเลย — ทุกช่องมีค่าเท่าเดิมหรือน้อยกว่าที่เก็บไว้แล้ว
                  ถ้าไม่ได้ตั้งใจ ให้เช็คว่าเลือกไฟล์รอบเก่าอยู่หรือเปล่า
                </p>
              )}

              <p className="meta">
                กติกา: <strong>ในแต่ละวันเก็บค่ามากสุด</strong> ค่าที่น้อยกว่าไม่ทับ —
                อัปไฟล์รอบเก่าทับรอบใหม่จึงไม่ทำให้ก้าวที่เดินจริงหายไป
              </p>
            </div>
          )}
        </section>
      )}

      {/* ── จับคู่ชื่อในแอปกับรหัสพนักงาน ── */}
      <MappingCard
        writable={writable}
        reloadKey={result?.importId ?? 0}
        onChanged={async () => {
          await Promise.all([loadCoverage(), loadBoard()])
        }}
      />

      {/* ── ประวัติการอัป ── */}
      <section className="card">
        <SectionHead
          title="ประวัติการ Upload"
          meta={
            coverage && coverage.dayRows > 0
              ? `ข้อมูลที่มี: ${coverage.firstDay} – ${coverage.lastDay} · ${nf.format(coverage.people)} คน · ${nf.format(coverage.dayRows)} ช่อง`
              : 'ยังไม่มีข้อมูล — อัปไฟล์แรกก่อน'
          }
          onReload={() => {
            void loadCoverage()
            void loadImports()
          }}
          loading={impLoading}
        />

        <SortBar
          sort={impSort.sort}
          labels={{ id: '#', fileName: 'ไฟล์', rowCount: 'คน', dayCount: 'วัน', periodFrom: 'ช่วงวันที่', importedBy: 'ผู้อัป', importedAt: 'เวลาที่อัป' }}
          onClear={() => impSort.setSort([])}
        />
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <SortTh label="#" sortKey="id" sort={impSort.sort} onToggle={impSort.toggle} />
                <SortTh label="ไฟล์" sortKey="fileName" sort={impSort.sort} onToggle={impSort.toggle} />
                <SortTh label="คน" sortKey="rowCount" sort={impSort.sort} onToggle={impSort.toggle} className="nowrap" />
                <SortTh label="วัน" sortKey="dayCount" sort={impSort.sort} onToggle={impSort.toggle} className="nowrap" />
                <SortTh label="ช่วงวันที่" sortKey="periodFrom" sort={impSort.sort} onToggle={impSort.toggle} className="nowrap" />
                <SortTh label="ผู้อัป" sortKey="importedBy" sort={impSort.sort} onToggle={impSort.toggle} />
                <SortTh label="เวลาที่อัป" sortKey="importedAt" sort={impSort.sort} onToggle={impSort.toggle} className="nowrap" />
              </tr>
            </thead>
            <tbody>
              {!imports || imports.imports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="meta">
                    {imports ? 'ยังไม่มีประวัติการ Upload' : 'กำลังโหลด…'}
                  </td>
                </tr>
              ) : (
                imports.imports.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.fileName ?? '-'}</td>
                    <td className="nowrap">{nf.format(r.rowCount)}</td>
                    <td className="nowrap">{r.dayCount}</td>
                    <td className="nowrap">
                      {r.periodFrom} – {r.periodTo}
                    </td>
                    <td>{r.importedBy}</td>
                    <td className="nowrap">{formatUtcAsThai(r.importedAtUtc)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {imports && (
          <Pager
            page={imports.page}
            pageSize={imports.pageSize}
            total={imports.total}
            loading={impLoading}
            onPage={setImpPage}
            unit="ครั้ง"
          />
        )}
      </section>

      {/* ── อันดับ ── */}
      <section className="card">
        <SectionHead
          title="อันดับรวม"
          meta={
            board ? `${nf.format(board.total)} คน · รวม ${nf.format(board.totalSteps)} ก้าว` : undefined
          }
          onReload={() => void loadBoard()}
          loading={boardLoading}
        />
        <p className="meta">
          คิดจากก้าวรายวันที่เก็บไว้ ไม่ได้ใช้คอลัมน์ <code>Total Steps</code> ในไฟล์ เพราะไฟล์
          แต่ละรอบนับช่วงไม่เท่ากัน · ตัวเลขสรุปข้างบนเป็นยอด<strong>ทั้งช่วง</strong> ไม่ใช่แค่หน้านี้
        </p>

        <div className="toolbar">
          <label>
            ตั้งแต่
            <DatePicker value={from} onChange={(v) => changeRange({ from: v })} max={to === '' ? undefined : to} />
          </label>
          <label>
            ถึง
            <DatePicker value={to} onChange={(v) => changeRange({ to: v })} min={from === '' ? undefined : from} />
          </label>
          {(from !== '' || to !== '') && (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setFrom('')
                setTo('')
                setBoardPage(1)
              }}
            >
              ล้างช่วงวันที่
            </button>
          )}
        </div>

        {!board ? (
          <p className="meta">กำลังโหลด…</p>
        ) : board.leaderboard.length === 0 ? (
          <p className="meta">ไม่มีข้อมูลในช่วงวันที่นี้</p>
        ) : (
          <>
            <SortBar
              sort={boardSort.sort}
              labels={{ name: 'ชื่อ', steps: 'ก้าวรวม', days: 'จำนวนวัน', firstDay: 'ช่วงที่มีข้อมูล' }}
              onClear={() => boardSort.setSort([])}
            />
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {/* "อันดับ" เรียงไม่ได้โดยตั้งใจ — เป็นลำดับที่นับจากหน้าปัจจุบัน
                        ไม่ใช่ข้อมูลในฐาน กดเรียงแล้วจะได้ 1,2,3 เหมือนเดิมทุกครั้ง
                        กม. ก็เช่นกัน คำนวณจากก้าวที่หน้าจอ เรียงตามก้าวได้ผลเดียวกัน */}
                    <th className="nowrap">อันดับ</th>
                    <SortTh label="ชื่อ" sortKey="name" sort={boardSort.sort} onToggle={boardSort.toggle} />
                    <SortTh label="ก้าวรวม" sortKey="steps" sort={boardSort.sort} onToggle={boardSort.toggle} className="nowrap" />
                    <th className="nowrap">กม. (ประมาณ)</th>
                    <SortTh label="จำนวนวัน" sortKey="days" sort={boardSort.sort} onToggle={boardSort.toggle} className="nowrap" />
                    <SortTh label="ช่วงที่มีข้อมูล" sortKey="firstDay" sort={boardSort.sort} onToggle={boardSort.toggle} className="nowrap" />
                  </tr>
                </thead>
                <tbody>
                  {board.leaderboard.map((r, i) => (
                    <tr key={r.nameKey}>
                      {/* อันดับต้องบวก offset ของหน้า ไม่ใช่ลำดับในหน้า */}
                      <td className="nowrap">{rankOffset + i + 1}</td>
                      <td>{r.name}</td>
                      <td className="nowrap">{nf.format(r.steps)}</td>
                      <td className="nowrap">{(r.steps * KM_PER_STEP).toFixed(2)}</td>
                      <td className="nowrap">{r.days}</td>
                      <td className="nowrap">
                        {r.firstDay === r.lastDay ? r.firstDay : `${r.firstDay} – ${r.lastDay}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {board && (
          <Pager
            page={board.page}
            pageSize={board.pageSize}
            total={board.total}
            loading={boardLoading}
            onPage={setBoardPage}
            unit="คน"
            onPageSize={(n) => {
              setBoardSize(n)
              setBoardPage(1)
            }}
          />
        )}
      </section>
    </>
  )
}
